import React, { useCallback, useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
} from 'recharts';
import concesionariaVentaProduccionService from '../../../services/concesionariaVentaProduccionService';
import {
  columnRouteLabel,
  defaultDashboardRange,
  formatDateDisplay,
  formatMoneyPen,
  formatQty,
  formatPos,
} from './ventaProduccionDashboardUtils';

export default function VentaProduccionDashboardPanel({
  scope = 'plantilla',
  plantillaId,
  pointOfSaleId,
  idPrefix = 'vp-dash',
  onLoadError,
}) {
  const [dashboard, setDashboard] = useState(null);
  const [range, setRange] = useState(() => defaultDashboardRange());
  const [loading, setLoading] = useState(true);

  const canLoad =
    scope === 'general' ||
    (scope === 'plantilla' && plantillaId) ||
    (scope === 'pos' && pointOfSaleId);

  const loadDashboard = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      let res;
      if (scope === 'general') {
        res = await concesionariaVentaProduccionService.getDashboardGeneral(range);
      } else if (scope === 'pos') {
        res = await concesionariaVentaProduccionService.getDashboardPos(pointOfSaleId, range);
      } else {
        res = await concesionariaVentaProduccionService.getPlantillaDashboard(plantillaId, range);
      }
      if (res.success && res.data) {
        setDashboard(res.data);
      } else {
        setDashboard(null);
      }
    } catch (e) {
      setDashboard(null);
      onLoadError?.(e.response?.data?.message || 'No se pudo cargar el dashboard');
    } finally {
      setLoading(false);
    }
  }, [
    canLoad,
    scope,
    plantillaId,
    pointOfSaleId,
    range.from_date,
    range.to_date,
    onLoadError,
  ]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const kpis = dashboard?.kpis;
  const monthly = dashboard?.monthly || [];
  const daily = dashboard?.daily || [];
  const meta = dashboard?.meta;
  const cuadro = dashboard?.cuadro;
  const cuadroSummary = cuadro?.summary;
  const showColumnBreakdown = scope === 'plantilla';

  const qtyTooltip = (value, name) => [formatQty(value), name];
  const pctTooltip = (value) => [`${Number(value).toFixed(1)}%`, 'Merma %'];

  if (!canLoad) {
    return null;
  }

  if (loading) {
    return <p className="text-gray-500 py-6">Cargando dashboard…</p>;
  }

  if (!dashboard) {
    return <p className="text-gray-500 py-6">Sin datos para mostrar.</p>;
  }

  const contextLabel =
    scope === 'general'
      ? 'Vista general: todos los puntos de venta a los que tiene acceso.'
      : scope === 'pos' && dashboard.point_of_sale
        ? `Punto de venta: ${formatPos(dashboard.point_of_sale)}`
        : null;

  return (
    <>
      {contextLabel && (
        <p className="mb-4 text-sm text-gray-600 rounded-md border border-gray-200 bg-white px-3 py-2">
          {contextLabel}
        </p>
      )}

      <section className="flex flex-wrap items-end gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50/80">
        <div>
          <label htmlFor={`${idPrefix}-from`} className="block text-xs font-medium text-gray-600 mb-1">
            Desde
          </label>
          <input
            id={`${idPrefix}-from`}
            type="date"
            value={range.from_date}
            onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-to`} className="block text-xs font-medium text-gray-600 mb-1">
            Hasta
          </label>
          <input
            id={`${idPrefix}-to`}
            type="date"
            value={range.to_date}
            onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white"
          />
        </div>
        <button
          type="button"
          onClick={loadDashboard}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-teal-600 text-teal-700 hover:bg-teal-50"
        >
          Actualizar
        </button>
        {meta && (
          <p className="text-xs text-gray-500 ml-auto">
            Período {formatDateDisplay(meta.from_date)} — {formatDateDisplay(meta.to_date)}
          </p>
        )}
      </section>

      {kpis && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cuadros</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{kpis.sheets_total}</p>
            <p className="text-xs text-gray-500 mt-1">
              {kpis.sheets_confirmed} confirmados · {kpis.sheets_draft} borrador
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Venta generada</p>
            <p className="mt-1 text-2xl font-semibold text-teal-800">{formatMoneyPen(kpis.sales_total_pen)}</p>
            <p className="text-xs text-gray-500 mt-1">Documentos de cuadros confirmados</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Documentos</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {kpis.documents_osv + kpis.documents_vt}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {kpis.documents_osv} OSV · {kpis.documents_vt} ventas
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Requerimiento</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{formatQty(kpis.requerimiento_total)}</p>
            <p className="text-xs text-gray-500 mt-1">Suma en filas del cuadro</p>
          </div>
        </div>
      )}

      {cuadroSummary && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900">Datos del cuadro (período)</h2>
          <p className="text-sm text-gray-500">
            {showColumnBreakdown
              ? 'Totales de filas del cuadro: requerimiento, columnas OSV (control), venta efectivo, diferencia y merma.'
              : 'Totales agregados entre plantillas: requerimiento, control OSV, diferencia y merma. El detalle por columna solo está en la vista por plantilla.'}
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-lg border border-orange-100 bg-orange-50/50 p-3">
              <p className="text-xs text-gray-600">Filas registradas</p>
              <p className="text-xl font-semibold text-gray-900">{cuadroSummary.filas}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-xs text-gray-600">Σ columnas OSV</p>
              <p className="text-xl font-semibold text-gray-900">{formatQty(cuadroSummary.osv_columns)}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-xs text-gray-600">Σ venta efectivo</p>
              <p className="text-xl font-semibold text-gray-900">{formatQty(cuadroSummary.cash_sale)}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-xs text-gray-600">Diferencia (req − OSV)</p>
              <p className="text-xl font-semibold text-amber-900">{formatQty(cuadroSummary.diferencia)}</p>
            </div>
            <div className="rounded-lg border border-rose-100 bg-rose-50/50 p-3">
              <p className="text-xs text-gray-600">Merma prom.</p>
              <p className="text-xl font-semibold text-rose-900">
                {cuadroSummary.merma_promedio_pct != null
                  ? `${Number(cuadroSummary.merma_promedio_pct).toFixed(1)}%`
                  : cuadroSummary.merma_pct != null
                    ? `${Number(cuadroSummary.merma_pct).toFixed(1)}%`
                    : '—'}
              </p>
            </div>
          </div>

          {(showColumnBreakdown && cuadro?.column_totals?.length > 0) || cuadro?.by_destino?.length > 0 ? (
            <div
              className={`mt-6 grid gap-6 ${
                showColumnBreakdown && cuadro?.column_totals?.length > 0 && cuadro?.by_destino?.length > 0
                  ? 'lg:grid-cols-2'
                  : ''
              }`}
            >
              {showColumnBreakdown && cuadro.column_totals?.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                  <h3 className="px-4 py-3 text-sm font-semibold text-gray-800 bg-gray-50 border-b border-gray-100">
                    Por columna
                  </h3>
                  <table className="min-w-full text-sm">
                    <tbody>
                      {cuadro.column_totals.map((col) => (
                        <tr key={col.column_key} className="border-t border-gray-100">
                          <td className="px-4 py-2 text-gray-800">{col.label}</td>
                          <td className="px-4 py-2 text-gray-500 text-xs">{columnRouteLabel(col.sale_route)}</td>
                          <td className="px-4 py-2 text-right font-medium">{formatQty(col.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {cuadro.by_destino?.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                  <h3 className="px-4 py-3 text-sm font-semibold text-gray-800 bg-gray-50 border-b border-gray-100">
                    Por destino
                  </h3>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-4 py-2 font-medium">Destino</th>
                        <th className="px-4 py-2 font-medium text-right">Req.</th>
                        <th className="px-4 py-2 font-medium text-right">Dif.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cuadro.by_destino.map((row) => (
                        <tr key={row.area_id} className="border-t border-gray-100">
                          <td className="px-4 py-2 text-gray-800">{row.name}</td>
                          <td className="px-4 py-2 text-right">{formatQty(row.requerimiento)}</td>
                          <td className="px-4 py-2 text-right text-amber-800">{formatQty(row.diferencia)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </section>
      )}

      {(daily.length > 0 || monthly.length > 0) && (
        <section className="mt-8 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Gráficos</h2>

          <div className="grid gap-6 lg:grid-cols-2">
            {daily.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900">Producción diaria (cuadro)</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Requerimiento vs suma de columnas OSV (control de producción por día).
                </p>
                <div className="mt-4 h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={48} />
                      <Tooltip formatter={qtyTooltip} />
                      <Legend />
                      <Bar dataKey="requerimiento" name="Requerimiento" fill="#64748b" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="produccion_osv" name="Producción (Σ OSV)" fill="#0d9488" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {daily.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900">Merma diaria</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Diferencia (unidades) y merma % por fecha de cuadro.
                </p>
                <div className="mt-4 h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={daily} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="qty" tick={{ fontSize: 11 }} width={44} />
                      <YAxis
                        yAxisId="pct"
                        orientation="right"
                        tick={{ fontSize: 11 }}
                        width={40}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'Merma %' ? pctTooltip(value) : qtyTooltip(value, name)
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId="qty"
                        dataKey="diferencia"
                        name="Diferencia"
                        fill="#f59e0b"
                        radius={[3, 3, 0, 0]}
                      />
                      <Line
                        yAxisId="pct"
                        type="monotone"
                        dataKey="merma_pct"
                        name="Merma %"
                        stroke="#e11d48"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {daily.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:col-span-2">
                <h3 className="text-base font-semibold text-gray-900">Venta en efectivo y venta (S/) por día</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Cantidad en columnas de efectivo vs monto de documentos del cuadro confirmado.
                </p>
                <div className="mt-4 h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={daily} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="qty" tick={{ fontSize: 11 }} width={44} />
                      <YAxis
                        yAxisId="pen"
                        orientation="right"
                        tick={{ fontSize: 11 }}
                        width={56}
                        tickFormatter={(v) => `S/${v}`}
                      />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'Ventas (S/)' ? formatMoneyPen(value) : qtyTooltip(value, name)
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId="qty"
                        dataKey="cash_sale"
                        name="Venta efectivo (uds.)"
                        fill="#6366f1"
                        radius={[3, 3, 0, 0]}
                      />
                      <Line
                        yAxisId="pen"
                        type="monotone"
                        dataKey="sales_pen"
                        name="Ventas (S/)"
                        stroke="#0d9488"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {monthly.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:col-span-2">
                <h3 className="text-base font-semibold text-gray-900">Venta por mes</h3>
                <p className="text-sm text-gray-500">Montos de documentos activos en cuadros confirmados.</p>
                <div className="mt-4 h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={72} />
                      <Tooltip formatter={(value) => formatMoneyPen(value)} />
                      <Bar dataKey="sales_pen" name="Ventas (S/)" fill="#0d9488" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
